import React, { useState, useEffect } from "react";
import authFetch from '../api';
import { useNavigate } from "react-router-dom";
import { PlusCircle, FolderOpen } from "lucide-react";
import { useTranslation } from "react-i18next";

const AdminProjects = () => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    powerCapacity: "",
    constructionType: "",
    notificationThresholds: [],
    dueDate: "",
  });

  const [activeTab, setActiveTab] = useState("active");

  // Use stored percentCompleted from database instead of recalculating
  const projectsWithCompletion = projects.map(project => {
    return {
      ...project,
      percentCompleted: project.percentCompleted || 0
    };
  });

  // Filter projects - use both completion percentage and status
  const activeProjects = projectsWithCompletion.filter((p) => {
    const isActive = p.percentCompleted < 100 || 
           (p.activities || []).length === 0 || 
           p.status !== "Completed";
    
    return isActive;
  });
  
  const completedProjects = projectsWithCompletion.filter((p) => {
    const isCompleted = (p.percentCompleted === 100 && (p.activities || []).length > 0) || 
           p.status === "Completed";
    
    return isCompleted;
  });

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if user is logged in
      const token = localStorage.getItem('jwtToken');
      const user = localStorage.getItem('user');
      
      if (!token) {
        setError(t('adminProjects.noAuthToken'));
        setLoading(false);
        return;
      }
      
      const res = await authFetch('/projects/assigned');
      
      // Handle both direct response and axios response format
      let projectsData;
      if (res && res.data) {
        // Axios response format
        projectsData = res.data;
      } else {
        // Direct response format
        projectsData = res;
      }
      
      if (Array.isArray(projectsData)) {
        setProjects(projectsData);
      } else {
        setProjects([]);
      }
    } catch (err) {
      console.error("Failed to load projects", err);
      
      if (err.response?.status === 401) {
        setError(t('adminProjects.authenticationFailed'));
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('user');
      } else {
        setError(t('adminProjects.failedToLoad'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      const res = await authFetch.post('/projects', formData);
      if (res.status !== 200 && res.status !== 201) {
        const data = res.data;
        alert(data.message || "Failed to create project");
        return;
      }
      setFormData({
        name: "",
        powerCapacity: "",
        constructionType: "",
        notificationThresholds: [],
        dueDate: "",
      });
      setShowModal(false);
      fetchProjects();
    } catch (err) {
      console.error("Error creating project", err);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  // Show loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center pt-16">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('adminProjects.projectManagement')}</h1>
            <p className="text-gray-600 mt-2">{t('adminProjects.manageDescription')}</p>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('adminProjects.loadingProjects')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center pt-16">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('adminProjects.projectManagement')}</h1>
            <p className="text-gray-600 mt-2">{t('adminProjects.manageDescription')}</p>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="text-center py-8">
            <div className="text-red-600 text-lg mb-4">{error}</div>
            <div className="space-x-4">
              <button
                onClick={fetchProjects}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                {t('adminProjects.tryAgain')}
              </button>
              <button
                onClick={() => window.location.href = '/login'}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors"
              >
                {t('adminProjects.goToLogin')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center pt-16">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('adminProjects.projectManagement')}</h1>
          <p className="text-gray-600 mt-2">{t('adminProjects.manageDescription')}</p>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
        <div className="flex justify-between items-center">
          <div className="flex space-x-2">
            <button
              onClick={() => setActiveTab('active')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                activeTab === 'active' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('adminProjects.active')}
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                activeTab === 'completed' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {t('adminProjects.completed')}
            </button>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
          >
            <PlusCircle size={16} /> {t('adminProjects.createProject')}
          </button>
        </div>
      </div>

      {/* Projects Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-blue-100">
              <tr>
                <th className="w-4" />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('adminProjects.projectName')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('adminProjects.powerCapacity')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('adminProjects.constructionType')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('adminProjects.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('adminProjects.percentCompleted')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(() => {
                let currentProjects = activeTab === "active" ? activeProjects : completedProjects;
                
                if (currentProjects.length === 0) {
                  return (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center">
                        <FolderOpen className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                        <h3 className="text-sm font-medium text-gray-900 mb-2">
                          {activeTab === 'active' 
                            ? t('adminProjects.noActiveProjects')
                            : t('adminProjects.noCompletedProjects')
                          }
                        </h3>
                        <p className="text-sm text-gray-500">
                          {activeTab === 'active' 
                            ? t('adminProjects.getStartedMessage')
                            : t('adminProjects.noCompletedMessage')
                          }
                        </p>
                      </td>
                    </tr>
                  );
                }
                
                return currentProjects.map((project) => {
                  const effectiveStatus =
                    project.percentCompleted === 100 ? "completed" : project.status;
                  
                  // Use the stored percentCompleted from database instead of recalculating
                  const percentCompleted = project.percentCompleted || 0;
                return (
                  <tr
                    key={project._id}
                    className={`group cursor-pointer transition-all duration-150 hover:bg-gray-50 ${
                      project.percentCompleted === 100
                        ? "bg-green-50"
                        : project.status === "At Risk"
                        ? "bg-red-50"
                        : project.status === "On Track"
                        ? "bg-yellow-50"
                        : project.status === "Not Started"
                        ? "bg-gray-50"
                        : "bg-white"
                    }`}
                    onClick={() => navigate(`/admin/dashboard/projects/${project._id}`)}
                  >
                    <td className="w-4 px-6 py-4">
                      <span className="invisible group-hover:visible text-blue-600 font-bold">→</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {project.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {project.powerCapacity}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {project.constructionType === 'Static' ? t('constructionType.static') : 
                       project.constructionType === 'Tracker' ? t('constructionType.tracker') : 
                       project.constructionType}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {effectiveStatus === 'On Track' ? t('projectStatus.onTrack') :
                       effectiveStatus === 'At Risk' ? t('projectStatus.atRisk') :
                       effectiveStatus === 'Completed' || effectiveStatus === 'completed' ? t('projectStatus.completed') :
                       effectiveStatus === 'Not Started' ? t('projectStatus.notStarted') :
                       effectiveStatus}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 flex justify-between items-center">
                      {Math.round(percentCompleted)}%
                      <span className="invisible group-hover:visible text-blue-600 font-bold">←</span>
                    </td>
                  </tr>
                );
              });
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Project Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">{t('adminProjects.addNewProject')}</h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>
              <form onSubmit={handleCreateProject} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('adminProjects.projectName')}</label>
                  <input
                    type="text"
                    placeholder={t('adminProjects.enterProjectName')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('adminProjects.powerCapacity')}</label>
                  <input
                    type="text"
                    placeholder={t('adminProjects.enterPowerCapacity')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    value={formData.powerCapacity}
                    onChange={(e) => setFormData({ ...formData, powerCapacity: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('adminProjects.constructionType')}</label>
                  <input
                    type="text"
                    placeholder={t('adminProjects.enterConstructionType')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    value={formData.constructionType}
                    onChange={(e) => setFormData({ ...formData, constructionType: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('adminProjects.dueDate')}</label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                    value={formData.dueDate}
                    onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('adminProjects.notificationThresholds')}</label>
                  <div className="flex gap-3 flex-wrap">
                    {[25, 50, 75, 100].map((value) => (
                      <label key={value} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          value={value}
                          checked={formData.notificationThresholds.includes(value)}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            setFormData((prev) => ({
                              ...prev,
                              notificationThresholds: e.target.checked
                                ? [...prev.notificationThresholds, val]
                                : prev.notificationThresholds.filter((t) => t !== val),
                            }));
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        {value}%
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    {t('adminProjects.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    {t('adminProjects.createProject')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProjects;